import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GET, POST } from '../../utils/request';
import PageLoading from '../../components/PageLoading/PageLoading';
import Question from '../../components/questions/Question';
import { getDefaultValue } from '../../utils/questions';
import { Button } from '@mui/material';
import CuestionarioNoDisponible from './CuestionarioNoDisponible/CuestionarioNoDisponible';
import CuestionarioYaRespondido from './CuestionarioYaRespondido/CuestionarioYaRespondido';
import Stepper from '../../components/Stepper/Stepper';
import css from './Cuestionario.module.css';

const Cuestionario = ()=> {

  const { alumnoId } = useParams()
  const navigate = useNavigate()

  const [cuestionario, setCuestionario] = useState([])
  const [step, setStep] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)

  const { isLoading, data: datos={} } = useQuery({
    queryKey: ['questions', alumnoId],
    queryFn: ()=> GET('/api/questions', { alumnoId })
      .catch((error)=> {
        setErrorMessage(error || 'Error al cargar el cuestionario')
        return {}
       })
  })

  const { isPending: isSubmitting, mutate: submit } = useMutation({
    mutationFn: data=> POST('/api/submit', {}, data),
    onSuccess: ()=> navigate('/gracias'),
    onError: (error)=> {
      alert(error || 'Error al enviar las respuestas. Por favor, inténtalo de nuevo.')
    }
  })

  useEffect(()=> {
    if (isLoading) return
    const preguntas = (datos.preguntas || []).map(pregunta=> {
      let defaultValue = getDefaultValue(pregunta)
      // For slider questions, initialize to the maximum value
      if (pregunta.type === 'slider' && pregunta.options) {
        const numericOptions = pregunta.options.map(opt => parseInt(opt, 10))
        defaultValue = Math.max(...numericOptions)
      }
      return { ...pregunta, value: defaultValue }
    })
    setCuestionario(preguntas)
  }, [isLoading, datos])

  const isQuestionAnswered = (pregunta) => {
    if (pregunta.optional) return true
    if (pregunta.type === 'multiple_choice') {
      return Array.isArray(pregunta.value) && pregunta.value.length > 0
    }
    return !!pregunta.value
  }

  const isCuestionaroCompleted = cuestionario.every(isQuestionAnswered)

  const firstUnanswered = cuestionario.findIndex(pregunta => !isQuestionAnswered(pregunta))

  const handleValueChange = (index, value)=> {
    setCuestionario(prevState=> {
      const newState = [...prevState]
      newState[index].value = value
      return newState
    })
    const pregunta = cuestionario[index]
    if (['face', 'radio', 'star'].includes(pregunta.type)) {
      setStep(s=> s + 1)
    }
  }

  const handleSubmit = ()=> {
    if (isSubmitting) return
    const datosEnvio = {
      alumnoId,
      version: datos.version,
      profesores: datos.profesores,
      cuestionario,
    }
    submit(datosEnvio)
  }

  if (errorMessage) {
    if (errorMessage.includes('Ya se ha respondido a la encuesta')) {
      return <CuestionarioYaRespondido />
    }
    return <CuestionarioNoDisponible errorMessage={errorMessage} />
  }

  return (
    <PageLoading isLoading={isLoading}>
      <div className={css.main}>
        <Stepper
          intro={null}
          questions={
            cuestionario.map((pregunta, index)=> (
              <Question
                key={index}
                type={pregunta.type}
                question={pregunta.question}
                options={pregunta.options}
                optional={pregunta.optional}
                value={pregunta.value}
                onChange={(value)=> handleValueChange(index, value)}
              />
            ))
          }
          end={(
            <div className={css.submit}>
              {!isCuestionaroCompleted && (
                <p className={css.message}>
                  🙈 ¡Vaya! Te falta alguna{' '}
                  <span className={css.highlight} onClick={()=> setStep(firstUnanswered)}>
                    pregunta
                  </span>
                  {' '}por contestar.
                </p>
              )}
              {isCuestionaroCompleted && (
                <p className={css.message}>
                  ¡Muchas gracias por tu participación! 😊
                </p>
              )}
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                disabled={!isCuestionaroCompleted || isSubmitting}
                onClick={handleSubmit}
              >
                Enviar respuestas
              </Button>
            </div>
          )}
          step={step}
          onNext={()=> setStep(s=> s + 1)}
          onPrevious={()=> setStep(s=> s - 1)}
        />
      </div>
    </PageLoading>
  );
}

export default Cuestionario;
